import { Controller } from "@hotwired/stimulus"
import { currencySymbol } from "../currency.js"

// V2 of the public pricing calculator at /pricing.
// Uses a 4-question admin diagnostic that scales with member count,
// splits price and value into distinct cards, and dynamically reveals
// plan features as the visitor toggles between tiers.
//
// Time model assumptions (per-task, before/after GolfTap):
//   - Phone booking:      4 min manual → 1 min on platform (3 min saved/booking)
//   - Walk-in booking:    3 min manual → 1 min on digital check-in (2 min saved/walk-in)
//   - Renewal cycle:      visitor's reported minutes → 1 min automated
//   - Competition:        90 min manual → 20 min on platform (70 min saved/comp)
//   - Manual finance:     4 hrs/month saved when automated
//
// Volume baseline (drives the natural scaling effect):
//   - Bookings/month assumed at 0.6 × members (~7.2 rounds/year/member, /12).
//   - Renewals/year = members (one renewal cycle per member).
//   - Competition entries handled separately — comp count, not member count.
export default class extends Controller {
  static targets = [
    // Tier toggle
    "tierTeesheet", "tierPlatform", "tierDesc",
    // Members
    "membersInput", "membersSlider",
    // Questionnaire — booking mix sliders stay precise (visitors are comfortable
    // estimating these percentages; the math benefits from the granularity).
    // All three are real sliders now; syncBookingMix keeps them summing to 100.
    "phonePctSlider", "phonePctValue",
    "walkinPctSlider", "walkinPctValue",
    "onlinePctSlider", "onlinePctValue",
    // Legacy precise inputs — kept guarded so removing them from the view
    // (in favour of segmented choices) doesn't break the math during/after
    // the survey-style refactor.
    "renewalMinutesSlider", "renewalMinutesValue",
    "compsSlider", "compsValue",
    "manualFinance",
    // Survey-style segmented choices replacing the three legacy inputs above.
    // Each "Group" target wraps a row of "Button" targets carrying a
    // data-value attribute; the controller mirrors the active value into
    // a hidden form field for posting to demo_requests.
    "clubTypeSelect",
    "renewalStyleGroup", "renewalStyleButton",
    "compsStyleGroup", "compsStyleButton",
    "reportingStyleGroup", "reportingStyleButton",
    // Sidebar snapshot extras — single consolidated card
    "topTimeSaversList", "setupFitPill", "setupFitText",
    "priceBasisMembers", "priceBasisTier", "backHoursPerYear",
    // Feature grid (unified — base + platform shown together, greyed when inactive)
    "featureCard",
    // Price card — band tease only (exact figures live behind the lead form)
    "priceBand",
    "priceAnnual", "pricePerMember", "priceMonthlyEquivalent",
    // Dev-only rate-rules listing (per current tier)
    "rateBandsList",
    // Quote-mode UI — visible/text changes for clubs >= QUOTE_MIN_MEMBERS
    "quoteBanner", "submitButton", "firstBullet",
    // Value card
    "todayHours", "withHours", "backHours",
    "valueAnnual", "valuePayback",
    // Diagnostic chips
    "chipsContainer",
    // Multi-select current systems — checkboxes write a comma-joined value
    // into the `currentSystemField` hidden input that posts to the server.
    "currentSystemField", "currentSystemCheckbox",
    // Hidden form fields
    "tierField", "membersField", "hoursPwField", "annualFeeField",
    "hoursSavedPmField", "roiMultipleField", "cppField",
    "bandBreakdownField", "questionsField",
    // Survey hidden fields — written on every update() so a form submit
    // carries the full operational profile to the demo_requests row.
    "surveyClubTypeField",
    "surveyBookingPhonePctField", "surveyBookingWalkinPctField", "surveyBookingOnlinePctField",
    "surveyRenewalStyleField", "surveyCompetitionCadenceField", "surveyReportingStyleField",
    // Form lifecycle
    "form", "formArea", "confirm", "confirmMsg",
    // Exit-intent modal
    "exitModal", "exitModalCard", "exitForm", "exitConfirm",
    "exitHoursPreview",
    "exitTierField", "exitMembersField", "exitHoursPwField", "exitAnnualFeeField",
    "exitHoursSavedPmField", "exitRoiMultipleField", "exitCppField",
    "exitBandBreakdownField", "exitQuestionsField", "exitCurrentSystemField"
  ]

  static MIN_MONTHLY = { teesheet: 150, platform: 250 }
  // At or above this member count we send the visitor into "tailored quote"
  // mode — calculator messaging swaps to "we'll quote you" rather than
  // showing a self-service price. The number input (not the slider) accepts
  // up to MAX_MEMBERS so big clubs can still enter their real scale.
  static QUOTE_MIN_MEMBERS = 1000
  static MAX_MEMBERS = 10000
  static TEESHEET_BANDS = [
    { limit: 100,      rate: 0.70 },
    { limit: 250,      rate: 0.60 },
    { limit: 500,      rate: 0.50 },
    { limit: 1000,     rate: 0.40 },
    { limit: Infinity, rate: 0.30 }
  ]
  static PLATFORM_BANDS = [
    { limit: 100,      rate: 2.30 },
    { limit: 250,      rate: 1.00 },
    { limit: 500,      rate: 0.90 },
    { limit: 1000,     rate: 0.80 },
    { limit: Infinity, rate: 0.70 }
  ]

  // Per-task time model: minutes to do the task before vs. after GolfTap.
  static MINUTES = {
    phoneBookingBefore:  4,
    phoneBookingAfter:   1,
    walkinBookingBefore: 3,
    walkinBookingAfter:  1,
    renewalAfter:        1,         // GolfTap automates renewals to ~1 min
    competitionBefore:  90,
    competitionAfter:   20,
    financeBefore:     240,         // 4 hrs/month
    financeAfter:        0
  }
  static HOURLY_RATE = 20
  static BOOKINGS_PER_MEMBER_PER_MONTH = 0.6

  static TIER_DESCS = {
    teesheet: "Simple, powerful tools for tee sheet, bookings and payments.<br>From €150/month minimum.",
    platform: "The complete club operations suite: tee sheet, CRM, competitions, events, POS, reporting and more.<br>From €250/month minimum."
  }

  // Segmented-choice → numeric-input maps. Each survey answer collapses a
  // spectrum of real-world behaviour onto a representative number that
  // feeds the existing calcValue math without changing its shape.
  static RENEWAL_MINUTES_BY_STYLE = {
    mostly_online: 2,    // member self-renews, staff just confirms
    mixed: 8,            // some chasing, some direct
    mostly_manual: 15    // full invoice + chase + record cycle
  }
  static COMPS_PER_MONTH_BY_CADENCE = {
    rarely: 1,           // occasional captain's prize, ~1/month avg
    weekly: 4,
    multiple_weekly: 10
  }
  // Reporting-style → monthly finance-reconciliation minutes saved.
  // Mostly-automated clubs save very little because their existing setup
  // already handles it; mostly-manual clubs save the full 4-hour window.
  static FINANCE_MINUTES_BY_STYLE = {
    mostly_automated: 0,
    mixed: 120,
    mostly_manual: 240
  }
  // Recommendation copy for the sidebar's setup-fit pill. The tier the
  // visitor has chosen drives the verdict; the club type tailors the
  // language. Defaults are deliberately encouraging — the goal is to
  // remove "I'm probably not the right fit" friction before the demo call.
  static SETUP_FIT_COPY = {
    teesheet: "Tee Sheet & Bookings is a great fit for clubs like yours.",
    platform: "Full Club Operations is the right scope for your setup."
  }

  connect() {
    this.currentTier = "platform"
    // Survey-style defaults — pre-selected so the snapshot card has a
    // meaningful estimate from page load. Visitor adjusts as they go.
    this.renewalStyle = "mixed"
    this.competitionCadence = "weekly"
    this.reportingStyle = "mixed"
    this.clubType = ""
    this.engaged = false
    this.exitShown = false
    this.submitted = false
    this.connectedAt = Date.now()
    this.applyTierStyles()
    this.applyFeatureGrid(false)
    this.renderRateBands()
    this.applySegmentedStyles()
    this.update()
    this.armExitIntent()
  }

  disconnect() {
    if (this.boundExitHandler) document.documentElement.removeEventListener("mouseleave", this.boundExitHandler)
    if (this.boundScrollHandler) window.removeEventListener("scroll", this.boundScrollHandler, { passive: true })
  }

  // Mark the visitor "engaged" the first time they touch any input or scroll.
  // Exit intent only fires for engaged visitors who haven't submitted.
  markEngaged() {
    this.engaged = true
  }

  // Wire up the document-level mouseleave handler. mouseleave on
  // <html> is the reliable cross-browser signal for "cursor left the
  // viewport" — mouseout fires constantly on element transitions and
  // is too noisy. Skip on touch devices — there's no meaningful
  // "leaving the viewport" gesture on mobile/tablet.
  armExitIntent() {
    if (this.isTouchDevice()) return
    if (this.alreadyShownThisSession()) { this.exitShown = true; return }

    // Scroll counts as engagement too — some visitors read first, slide later.
    this.boundScrollHandler = () => { this.markEngaged() }
    window.addEventListener("scroll", this.boundScrollHandler, { passive: true })

    this.boundExitHandler = (event) => {
      if (this.exitShown || this.submitted) return
      if (!this.engaged) return
      if ((Date.now() - this.connectedAt) < 3000) return // brief grace period
      // Only fire when cursor exits toward the top of the viewport (URL bar / tabs).
      if (event.clientY > 0) return
      this.showExitModal()
    }
    document.documentElement.addEventListener("mouseleave", this.boundExitHandler)
  }

  isTouchDevice() {
    return ("ontouchstart" in window) || navigator.maxTouchPoints > 0
  }

  alreadyShownThisSession() {
    try { return sessionStorage.getItem("gt_exit_intent_shown") === "1" }
    catch (_) { return false }
  }

  markExitShown() {
    this.exitShown = true
    try { sessionStorage.setItem("gt_exit_intent_shown", "1") } catch (_) {}
  }

  showExitModal() {
    if (!this.hasExitModalTarget) return
    this.markExitShown()
    this.copyHiddenFieldsToExitForm()

    // Friendly preview line ("Don't lose ~47 hours back")
    if (this.hasExitHoursPreviewTarget && this.hasBackHoursTarget) {
      const txt = this.backHoursTarget.textContent.trim()
      this.exitHoursPreviewTarget.textContent = (txt && txt !== "—") ? txt : "your"
    }

    this.exitModalTarget.classList.remove("hidden")
    this.exitModalTarget.classList.add("flex")
    // Tiny scale-in animation
    if (this.hasExitModalCardTarget) {
      this.exitModalCardTarget.style.transform = "scale(0.96)"
      this.exitModalCardTarget.style.opacity = "0"
      this.exitModalCardTarget.style.transition = "transform 220ms ease-out, opacity 220ms ease-out"
      requestAnimationFrame(() => {
        this.exitModalCardTarget.style.transform = "scale(1)"
        this.exitModalCardTarget.style.opacity = "1"
      })
    }
  }

  dismissExitModal() {
    if (!this.hasExitModalTarget) return
    this.exitModalTarget.classList.add("hidden")
    this.exitModalTarget.classList.remove("flex")
  }

  // Only dismiss when the click landed on the backdrop itself, not the card.
  dismissExitOnBackdrop(event) {
    if (event.target === this.exitModalTarget) this.dismissExitModal()
  }

  // Mirror current calculator state into the exit-modal form's hidden fields
  // so a submit there carries the same payload as the inline form.
  copyHiddenFieldsToExitForm() {
    const pairs = [
      ["tierField",            "exitTierField"],
      ["membersField",         "exitMembersField"],
      ["hoursPwField",         "exitHoursPwField"],
      ["annualFeeField",       "exitAnnualFeeField"],
      ["hoursSavedPmField",    "exitHoursSavedPmField"],
      ["roiMultipleField",     "exitRoiMultipleField"],
      ["cppField",             "exitCppField"],
      ["bandBreakdownField",   "exitBandBreakdownField"],
      ["questionsField",       "exitQuestionsField"],
      ["currentSystemField",   "exitCurrentSystemField"]
    ]
    for (const [src, dst] of pairs) {
      const srcEl = this[`has${this.cap(src)}Target`] ? this[`${src}Target`] : null
      const dstEl = this[`has${this.cap(dst)}Target`] ? this[`${dst}Target`] : null
      if (srcEl && dstEl) dstEl.value = srcEl.value
    }
  }

  cap(s) { return s.charAt(0).toUpperCase() + s.slice(1) }

  async submitExit(event) {
    event.preventDefault()
    if (!this.hasExitFormTarget) return
    const formData = new FormData(this.exitFormTarget)
    try {
      const response = await fetch(this.exitFormTarget.action, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: formData
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        const msg = (data.errors && data.errors.join(", ")) || "Something went wrong. Please try again."
        alert(msg)
        return
      }
    } catch (_) { /* show confirmation anyway */ }

    this.submitted = true
    this.exitFormTarget.classList.add("hidden")
    if (this.hasExitConfirmTarget) this.exitConfirmTarget.classList.remove("hidden")
    // Auto-close after a moment so they can keep reading the page
    setTimeout(() => this.dismissExitModal(), 2400)
  }

  setTeesheet() { this.setTier("teesheet") }
  setPlatform() { this.setTier("platform") }

  setTier(tier) {
    if (this.currentTier === tier) return
    this.currentTier = tier
    this.applyTierStyles()
    this.applyFeatureGrid(true)
    this.renderRateBands()
    this.update()
  }

  applyTierStyles() {
    // flex+gap-2 keeps the heroicon and label aligned inside each tab button.
    const active   = "flex-1 px-6 py-3 text-sm font-semibold rounded-lg transition-colors bg-[#deffad] text-[#4c642a] flex items-center justify-center gap-2"
    const inactive = "flex-1 px-6 py-3 text-sm font-semibold rounded-lg transition-colors text-gray-500 hover:text-gray-700 flex items-center justify-center gap-2"
    this.tierTeesheetTarget.className = this.currentTier === "teesheet" ? active : inactive
    this.tierPlatformTarget.className  = this.currentTier === "platform"  ? active : inactive
    this.tierDescTarget.innerHTML = this.constructor.TIER_DESCS[this.currentTier]
  }

  // Light up the cards in the chosen plan, grey out the rest. When the
  // tier changes, the cards that *just transitioned* get a small stagger
  // pulse so the visitor sees the bundle reorganise.
  applyFeatureGrid(animate) {
    if (!this.hasFeatureCardTarget) return
    const isPlatform = this.currentTier === "platform"
    const transitioned = []
    this.featureCardTargets.forEach(card => {
      const tier = card.dataset.featureTier
      const shouldBeActive = (tier === "base") || (tier === "platform" && isPlatform)
      const wasActive = card.classList.contains("is-active")
      card.classList.toggle("is-active", shouldBeActive)
      card.classList.toggle("is-inactive", !shouldBeActive)
      if (animate && wasActive !== shouldBeActive) transitioned.push(card)
    })
    if (animate && transitioned.length > 0) this.pulseStagger(transitioned)
  }

  pulseStagger(cards) {
    cards.forEach((el, i) => {
      el.style.transform = "scale(0.97)"
      el.style.transition = "transform 200ms ease-out, background-color 300ms ease-out, opacity 300ms ease-out"
      setTimeout(() => { el.style.transform = "scale(1)" }, i * 25)
    })
  }

  syncFromSlider() {
    this.markEngaged()
    this.membersInputTarget.value = this.membersSliderTarget.value
    this.update()
  }

  syncFromInput() {
    this.markEngaged()
    const v = Math.min(Math.max(parseInt(this.membersInputTarget.value) || 0, 0), this.constructor.MAX_MEMBERS)
    this.membersSliderTarget.value = v
    this.update()
  }

  clampOnBlur() {
    this.markEngaged()
    const v = Math.min(Math.max(parseInt(this.membersInputTarget.value) || 0, 0), this.constructor.MAX_MEMBERS)
    this.membersInputTarget.value = v
    this.membersSliderTarget.value = v
    this.update()
  }

  // All three booking-mix sliders must always sum to 100. When one
  // slider moves, the other two rebalance proportionally to absorb
  // the change. Falls back to even split if the other two are both
  // at zero (no proportion to preserve). Backwards-compatible with
  // the older two-slider layout — if onlinePctSlider isn't in the
  // DOM, online is treated as the residual the way it used to be.
  syncBookingMix(event) {
    this.markEngaged()
    if (this.hasOnlinePctSliderTarget) {
      this.rebalanceThreeWay(event?.target)
    } else {
      // Legacy two-slider behaviour: phone + walkin clamp to ≤ 100.
      const phone  = this.intVal(this.phonePctSliderTarget)
      const walkin = this.intVal(this.walkinPctSliderTarget)
      if (phone + walkin > 100) {
        if (event?.target === this.phonePctSliderTarget) {
          this.walkinPctSliderTarget.value = Math.max(0, 100 - phone)
        } else {
          this.phonePctSliderTarget.value = Math.max(0, 100 - walkin)
        }
      }
    }
    this.refreshBookingLabels()
    this.update()
  }

  // Distribute (100 - moved) across the other two sliders in proportion
  // to their current values. Rounds to the nearest 5% step (matching
  // the sliders' own step) and corrects rounding drift on the larger
  // of the two so the three always sum to exactly 100.
  rebalanceThreeWay(movedSliderEl) {
    const step = 5
    const moved = movedSliderEl || this.phonePctSliderTarget
    let movedVal = this.intVal(moved)
    if (movedVal < 0) movedVal = 0
    if (movedVal > 100) movedVal = 100
    moved.value = movedVal

    const others = [this.phonePctSliderTarget, this.walkinPctSliderTarget, this.onlinePctSliderTarget]
      .filter(s => s !== moved)
    const [otherA, otherB] = others
    let a = this.intVal(otherA)
    let b = this.intVal(otherB)
    const target = 100 - movedVal
    const otherSum = a + b

    if (target <= 0) {
      a = 0
      b = 0
    } else if (otherSum === 0) {
      // No proportions to preserve — split evenly, biased to otherA.
      a = Math.round((target / 2) / step) * step
      b = target - a
    } else {
      const ratio = target / otherSum
      a = Math.round((a * ratio) / step) * step
      b = target - a
      // If b ended up out of bounds due to rounding, nudge a.
      if (b < 0) { b = 0; a = target }
      if (b > 100) { b = 100; a = Math.max(0, target - b) }
    }

    otherA.value = Math.max(0, Math.min(100, a))
    otherB.value = Math.max(0, Math.min(100, b))
  }

  refreshBookingLabels() {
    const phone  = this.intVal(this.phonePctSliderTarget)
    const walkin = this.intVal(this.walkinPctSliderTarget)
    const online = this.hasOnlinePctSliderTarget
      ? this.intVal(this.onlinePctSliderTarget)
      : Math.max(0, 100 - phone - walkin)
    if (this.hasPhonePctValueTarget)  this.phonePctValueTarget.textContent  = `${phone}%`
    if (this.hasWalkinPctValueTarget) this.walkinPctValueTarget.textContent = `${walkin}%`
    if (this.hasOnlinePctValueTarget) {
      // Slider-style value when the live slider exists; residual hint
      // copy when it doesn't (older two-slider layout).
      this.onlinePctValueTarget.textContent = this.hasOnlinePctSliderTarget
        ? `${online}%`
        : `${online}% already online`
    }
  }

  // Gather every checked system, comma-join, and write the result into the hidden
  // `current_system` field so the server keeps storing a single string column.
  syncCurrentSystems() {
    this.markEngaged()
    if (!this.hasCurrentSystemFieldTarget) return
    const checked = (this.hasCurrentSystemCheckboxTarget ? this.currentSystemCheckboxTargets : [])
      .filter(cb => cb.checked)
      .map(cb => cb.value)
    this.currentSystemFieldTarget.value = checked.join(", ")
  }

  syncQuestionnaire() {
    this.markEngaged()
    if (this.hasRenewalMinutesValueTarget && this.hasRenewalMinutesSliderTarget) this.renewalMinutesValueTarget.textContent = `${this.intVal(this.renewalMinutesSliderTarget)} mins`
    if (this.hasCompsValueTarget && this.hasCompsSliderTarget)                   this.compsValueTarget.textContent          = this.intVal(this.compsSliderTarget).toLocaleString("en-IE")
    this.update()
  }

  // Q1 — club type dropdown change. Drives setup-fit copy + survey field.
  syncClubType() {
    this.markEngaged()
    if (this.hasClubTypeSelectTarget) this.clubType = this.clubTypeSelectTarget.value || ""
    this.update()
  }

  // Q4 / Q5 / Q6 — segmented choice buttons. Each button carries a
  // data-value attribute matching one of the static *_BY_STYLE keys.
  setRenewalStyle(event) {
    this.markEngaged()
    const v = event.currentTarget.dataset.value
    if (v) this.renewalStyle = v
    this.applySegmentedStyles()
    this.update()
  }

  setCompetitionCadence(event) {
    this.markEngaged()
    const v = event.currentTarget.dataset.value
    if (v) this.competitionCadence = v
    this.applySegmentedStyles()
    this.update()
  }

  setReportingStyle(event) {
    this.markEngaged()
    const v = event.currentTarget.dataset.value
    if (v) this.reportingStyle = v
    this.applySegmentedStyles()
    this.update()
  }

  // Light the active button in each segmented group; grey the rest.
  // Same green pill treatment used by the tier toggle so the page reads
  // consistently. Each group iterated independently so the controller
  // tolerates missing groups (e.g. legacy view still on sliders).
  applySegmentedStyles() {
    const apply = (buttons, currentValue) => {
      buttons.forEach(btn => {
        const isActive = btn.dataset.value === currentValue
        btn.classList.toggle("bg-[#deffad]", isActive)
        btn.classList.toggle("text-[#4c642a]", isActive)
        btn.classList.toggle("border-[#4c642a]", isActive)
        btn.classList.toggle("text-gray-500", !isActive)
        btn.classList.toggle("border-gray-200", !isActive)
        btn.classList.toggle("bg-white", !isActive)
      })
    }
    if (this.hasRenewalStyleButtonTarget) apply(this.renewalStyleButtonTargets, this.renewalStyle)
    if (this.hasCompsStyleButtonTarget)   apply(this.compsStyleButtonTargets,   this.competitionCadence)
    if (this.hasReportingStyleButtonTarget) apply(this.reportingStyleButtonTargets, this.reportingStyle)
  }

  // ─── Derived survey-driven inputs ────────────────────────────────────
  // Each getter prefers the segmented-choice value when the new view is
  // active, falls back to the legacy slider/checkbox when the old view
  // is still on screen. Keeps calcValue() oblivious to the swap.

  getRenewalMinutes() {
    if (this.hasRenewalMinutesSliderTarget) {
      const M = this.constructor.MINUTES
      return Math.max(this.intVal(this.renewalMinutesSliderTarget), M.renewalAfter)
    }
    return this.constructor.RENEWAL_MINUTES_BY_STYLE[this.renewalStyle] ?? 8
  }

  getCompsPerMonth() {
    if (this.hasCompsSliderTarget) return this.intVal(this.compsSliderTarget)
    return this.constructor.COMPS_PER_MONTH_BY_CADENCE[this.competitionCadence] ?? 4
  }

  // Returns minutes-per-month of finance reconciliation effort the club
  // currently spends. Legacy view used a binary checkbox (4 hrs or 0);
  // the survey view varies from 0 to 4 hrs based on the segmented style.
  getFinanceMinutesPerMonth() {
    if (this.hasManualFinanceTarget) {
      return this.manualFinanceTarget.checked ? this.constructor.MINUTES.financeBefore : 0
    }
    return this.constructor.FINANCE_MINUTES_BY_STYLE[this.reportingStyle] ?? 0
  }

  intVal(target) { return parseInt(target?.value) || 0 }

  calcFee(members) {
    if (members === 0) return { monthly: 0, annual: 0, cpp: 0, lines: [], minApplied: false }
    const bands = this.currentTier === "teesheet" ? this.constructor.TEESHEET_BANDS : this.constructor.PLATFORM_BANDS
    const minMonthly = this.constructor.MIN_MONTHLY[this.currentTier]
    let usageMonthly = 0
    let prev = 0
    const lines = []
    for (const b of bands) {
      if (members <= prev) break
      const inBand = Math.min(members, b.limit) - prev
      if (inBand <= 0) break
      const cost = inBand * b.rate
      usageMonthly += cost
      lines.push({ from: prev + 1, to: Math.min(members, b.limit), count: inBand, rate: b.rate, monthly: cost })
      prev = b.limit
    }
    const monthly = Math.max(minMonthly, usageMonthly)
    return { monthly, annual: monthly * 12, cpp: monthly / members, lines, minApplied: usageMonthly < minMonthly }
  }

  // Compute today's monthly minutes vs. with-GolfTap monthly minutes,
  // broken down per task. Tee Sheet plan only counts booking-related
  // savings; Full Platform counts all four.
  calcValue(members) {
    const M = this.constructor.MINUTES
    const isPlatform = this.currentTier === "platform"

    const phonePct       = this.intVal(this.phonePctSliderTarget) / 100
    const walkinPct      = this.intVal(this.walkinPctSliderTarget) / 100
    const renewalMinutes = Math.max(this.getRenewalMinutes(), M.renewalAfter)
    const comps          = this.getCompsPerMonth()
    const financeMinutes = this.getFinanceMinutesPerMonth()

    const bookingsPerMo  = members * this.constructor.BOOKINGS_PER_MEMBER_PER_MONTH
    const phonePerMo     = bookingsPerMo * phonePct
    const walkinPerMo    = bookingsPerMo * walkinPct

    const tasks = [
      {
        key: "phone",
        label: "Online bookings (phone replaced)",
        todayMin: phonePerMo * M.phoneBookingBefore,
        withMin:  phonePerMo * M.phoneBookingAfter,
        applies:  true
      },
      {
        key: "walkin",
        label: "Walk-in check-in tidied up",
        todayMin: walkinPerMo * M.walkinBookingBefore,
        withMin:  walkinPerMo * M.walkinBookingAfter,
        applies:  true
      },
      {
        key: "renewals",
        label: "Membership renewals automated",
        todayMin: (members * renewalMinutes) / 12,
        withMin:  (members * M.renewalAfter)  / 12,
        applies:  isPlatform
      },
      {
        key: "comps",
        label: "Competition admin",
        todayMin: comps * M.competitionBefore,
        withMin:  comps * M.competitionAfter,
        applies:  isPlatform
      },
      {
        key: "finance",
        label: "Finance reconciliation",
        todayMin: financeMinutes,
        // GolfTap fully automates reconciliation; remaining minutes is 0
        // when there was any manual effort to begin with, otherwise also 0.
        withMin:  0,
        applies:  isPlatform
      }
    ]

    let todayMin = 0, withMin = 0
    const contributions = []
    for (const t of tasks) {
      if (!t.applies) continue
      const saved = t.todayMin - t.withMin
      todayMin += t.todayMin
      withMin  += t.withMin
      if (saved > 0.05) {
        contributions.push({
          key: t.key,
          label: t.label,
          hrsPerMo: saved / 60,
          todayHrs: t.todayMin / 60,
          withHrs:  t.withMin  / 60
        })
      }
    }

    const todayHours = todayMin / 60
    const withHours  = withMin  / 60
    const backHours  = todayHours - withHours
    const valuePerYear = backHours * 12 * this.constructor.HOURLY_RATE

    return { todayHours, withHours, backHours, valuePerYear, contributions, isPlatform }
  }

  update() {
    const members = parseInt(this.membersInputTarget.value) || 0
    this.refreshBookingLabels()

    const fee      = this.calcFee(members)
    const value    = this.calcValue(members)
    const quoteOnly = members >= this.constructor.QUOTE_MIN_MEMBERS

    // Price card — show a band on the page; exact figures stay hidden until lead capture.
    // Legacy targets (priceAnnual / pricePerMember / priceMonthlyEquivalent) remain in the
    // targets list for back-compat but their DOM nodes have been removed from the public view.
    if (this.hasPriceBandTarget)              this.priceBandTarget.textContent              = members === 0 ? "—" : (quoteOnly ? "Tailored quote · 1,000+ members" : this.priceBandLabel(fee.monthly))
    if (this.hasPriceAnnualTarget)            this.priceAnnualTarget.textContent            = members > 0 ? `${currencySymbol()}${this.fmtInt(fee.annual)}` : "—"
    if (this.hasPricePerMemberTarget)         this.pricePerMemberTarget.textContent         = members > 0 ? `${currencySymbol()}${fee.cpp.toFixed(2)}` : "—"
    if (this.hasPriceMonthlyEquivalentTarget) this.priceMonthlyEquivalentTarget.textContent = members > 0 ? `${currencySymbol()}${this.fmtInt(fee.monthly)}` : "—"

    // Snapshot card — basis lines beneath the pricing headline so the
    // visitor sees what the estimate is built on without scrolling away.
    if (this.hasPriceBasisMembersTarget) {
      this.priceBasisMembersTarget.textContent = members > 0
        ? `Based on ${this.fmtInt(members)} member${members === 1 ? "" : "s"}`
        : "Tell us about your club"
    }
    if (this.hasPriceBasisTierTarget) {
      this.priceBasisTierTarget.textContent = this.currentTier === "teesheet" ? "Tee Sheet & Bookings" : "Full Club Operations"
    }

    // Snapshot — hours back per month (headline) + per-year tagline.
    // The "/ month" suffix lives in the markup span so the JS only
    // owns the numeric portion.
    if (this.hasTodayHoursTarget) this.todayHoursTarget.textContent = value.todayHours > 0 ? `~${Math.round(value.todayHours)} hrs` : "—"
    if (this.hasWithHoursTarget)  this.withHoursTarget.textContent  = value.todayHours > 0 ? `~${Math.round(value.withHours)} hrs`  : "—"
    if (this.hasBackHoursTarget)  this.backHoursTarget.textContent  = value.backHours > 0  ? `~${Math.round(value.backHours)} hrs`  : "—"
    if (this.hasBackHoursPerYearTarget) {
      this.backHoursPerYearTarget.textContent = value.backHours > 0
        ? `~${this.fmtInt(value.backHours * 12)} hrs per year`
        : "—"
    }
    if (this.hasValueAnnualTarget) this.valueAnnualTarget.textContent = value.valuePerYear > 0 ? `${currencySymbol()}${this.fmtInt(value.valuePerYear)}` : "—"

    if (this.hasValuePaybackTarget) {
      if (value.valuePerYear > 0 && fee.annual > 0) {
        const months = Math.max(0.5, (fee.annual / value.valuePerYear) * 12)
        this.valuePaybackTarget.textContent = months < 1
          ? "Pays for itself in under a month"
          : `Pays for itself in ~${months.toFixed(1)} months`
      } else {
        this.valuePaybackTarget.textContent = "Add a few details to see your payback"
      }
    }

    // Diagnostic chips — live "where the time goes" panel
    this.renderChips(value.contributions)

    // Sidebar snapshot extras (survey-style page only — guarded so the
    // legacy layout, which has neither target, is unaffected).
    const timeSavers = this.computeTopTimeSavers(value.contributions)
    this.renderTopTimeSavers(timeSavers)
    this.renderSetupFit()

    // Quote-mode UI swaps for clubs above the calculator's self-serve range
    this.applyQuoteState(quoteOnly)

    this.populateHiddenFields(members, fee, value, timeSavers)
  }

  // ─── Top Time Savers ─────────────────────────────────────────────────
  // Rule-driven list of the 3 most impactful operational pain points the
  // visitor has flagged. The phrasing is intentionally diagnostic rather
  // than promotional — these become the opening talking points for the
  // follow-up sales call.
  computeTopTimeSavers(contributions) {
    const items = []
    const phonePct = this.intVal(this.phonePctSliderTarget)
    const walkinPct = this.intVal(this.walkinPctSliderTarget)

    if (phonePct >= 40) items.push({ key: "phone_bookings", label: "High reliance on phone bookings" })
    if (walkinPct >= 30) items.push({ key: "walkin_volume", label: "Heavy walk-in volume to manage" })
    if (this.renewalStyle === "mostly_manual") items.push({ key: "manual_renewals", label: "Manual membership renewals" })
    if (this.competitionCadence === "multiple_weekly") items.push({ key: "comp_admin_heavy", label: "Competitions take significant admin time" })
    else if (this.competitionCadence === "weekly") items.push({ key: "comp_admin", label: "Weekly competition admin overhead" })
    if (this.reportingStyle === "mostly_manual") items.push({ key: "manual_reporting", label: "End-of-month reconciliations are manual" })
    else if (this.reportingStyle === "mixed") items.push({ key: "mixed_reporting", label: "Reporting spread across mixed systems" })

    // If the visitor's answers are tidy, fall back to the largest hour
    // contributions so the panel never sits empty — silence reads as
    // "we couldn't find anything to say about your club."
    if (items.length < 3 && contributions && contributions.length) {
      contributions
        .slice()
        .sort((a, b) => b.hrsPerMo - a.hrsPerMo)
        .slice(0, 3 - items.length)
        .forEach(c => items.push({ key: c.key, label: c.label }))
    }

    return items.slice(0, 3)
  }

  renderTopTimeSavers(items) {
    if (!this.hasTopTimeSaversListTarget) return
    if (items.length === 0) {
      this.topTimeSaversListTarget.innerHTML = `<p class="text-xs text-gray-500 italic">Tell us about your club above.</p>`
      return
    }
    this.topTimeSaversListTarget.innerHTML = items.map(item => `
      <div class="flex items-start gap-2 mb-2">
        <span class="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#deffad] text-[#4c642a] flex-shrink-0 mt-0.5">
          <svg class="w-3 h-3" fill="none" stroke="currentColor" stroke-width="3" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7"/>
          </svg>
        </span>
        <span class="text-sm text-gray-700">${this.escapeHtml(item.label)}</span>
      </div>
    `).join("")
  }

  // ─── Setup Fit ──────────────────────────────────────────────────────
  // One-line verdict matching the tier the visitor has chosen, optionally
  // tailored to club type when available. Always positive — the goal is
  // to remove "I'm probably the wrong fit" friction before they submit.
  renderSetupFit() {
    if (!this.hasSetupFitTextTarget) return
    const copy = this.constructor.SETUP_FIT_COPY[this.currentTier] || ""
    this.setupFitTextTarget.textContent = copy
  }

  // Re-create the inline form's hidden survey_top_time_savers[] inputs
  // on every update. Rails expects an array of strings on the demo_request
  // params, so we render one <input> per saver. The container <div> with
  // data-survey-top-time-savers-container lives in the form partial.
  syncTopTimeSaversInputs(savers) {
    if (!this.hasFormTarget) return
    const container = this.formTarget.querySelector("[data-survey-top-time-savers-container]")
    if (!container) return
    container.innerHTML = savers.map(s =>
      `<input type="hidden" name="demo_request[survey_top_time_savers][]" value="${this.escapeHtml(s.label)}">`
    ).join("")
  }

  // Toggle the form's CTA + first bullet + the warning banner so the visitor
  // understands their club is over the self-serve range and we'll prepare a
  // tailored quote rather than a generic breakdown. Everything else (hours
  // back, chips, value framing) stays — those are still real even at scale.
  applyQuoteState(quoteOnly) {
    if (this.hasQuoteBannerTarget) this.quoteBannerTarget.classList.toggle("hidden", !quoteOnly)
    if (this.hasFirstBulletTarget) {
      this.firstBulletTarget.innerHTML = quoteOnly
        ? '<strong class="text-gray-900">A tailored quote</strong> &mdash; calculated for your club\'s scale and operations'
        : '<strong class="text-gray-900">Your exact price</strong> &mdash; per member, per month, per year'
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.value = quoteOnly ? "Request my tailored quote →" : "Email me my breakdown →"
    }
  }

  // Dev-only rendering of the rate-band rules driving the calculator. Lets
  // the team eyeball "for the chosen tier, what's the per-member rate at each
  // band" without hopping into the source. Re-rendered when the tier toggles.
  renderRateBands() {
    if (!this.hasRateBandsListTarget) return
    const bands    = this.currentTier === "teesheet" ? this.constructor.TEESHEET_BANDS : this.constructor.PLATFORM_BANDS
    const minMo    = this.constructor.MIN_MONTHLY[this.currentTier]
    const tierName = this.currentTier === "teesheet" ? "Tee Sheet" : "Full Club Operations"
    let prev = 0
    const rows = bands.map(b => {
      const from  = prev + 1
      const range = b.limit === Infinity
        ? `${from.toLocaleString("en-IE")}+`
        : `${from.toLocaleString("en-IE")}–${b.limit.toLocaleString("en-IE")}`
      prev = b.limit
      return `<div class="grid grid-cols-2 gap-2"><span class="text-amber-700/70">${range}</span><span class="font-mono font-semibold text-amber-900 text-right">${currencySymbol()}${b.rate.toFixed(2)} /member/mo</span></div>`
    }).join("")
    this.rateBandsListTarget.innerHTML = `
      <p class="text-amber-700/80 mb-1.5">${tierName} · ${currencySymbol()}${minMo}/mo minimum</p>
      <div class="space-y-0.5">${rows}</div>
    `
  }

  renderChips(contributions) {
    if (!this.hasChipsContainerTarget) return
    if (contributions.length === 0) {
      this.chipsContainerTarget.innerHTML = `<p class="text-xs text-gray-500 italic">Move the sliders above to see where the time goes.</p>`
      return
    }
    this.chipsContainerTarget.innerHTML = contributions
      .sort((a, b) => b.hrsPerMo - a.hrsPerMo)
      .map(c => `
        <span class="inline-flex items-center gap-2 bg-[#deffad] text-[#3d5021] px-3 py-1.5 rounded-full text-xs font-medium">
          <span class="font-bold">${c.hrsPerMo.toFixed(1)} hrs/mo</span>
          <span class="opacity-80">from ${this.escapeHtml(c.label.toLowerCase())}</span>
        </span>
      `).join("")
  }

  escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
  }

  fmtInt(n) {
    return Math.round(n).toLocaleString("en-IE")
  }

  // Public-facing price tease — narrow enough to be useful (visitor knows roughly
  // what to expect), wide enough that they still need the email breakdown for the
  // exact number. Bands round outward from a typical small-club minimum.
  priceBandLabel(monthly) {
    if (monthly < 250)  return "Under €250 / month"
    if (monthly < 500)  return "€250 – €500 / month"
    if (monthly < 1000) return "€500 – €1,000 / month"
    if (monthly < 2000) return "€1,000 – €2,000 / month"
    return "€2,000+ / month"
  }

  populateHiddenFields(members, fee, value, timeSavers = []) {
    if (this.hasTierFieldTarget)         this.tierFieldTarget.value         = this.currentTier
    if (this.hasMembersFieldTarget)      this.membersFieldTarget.value      = members
    const hoursPerWeek = (value.backHours * 12) / 52
    if (this.hasHoursPwFieldTarget)      this.hoursPwFieldTarget.value      = hoursPerWeek > 0 ? hoursPerWeek.toFixed(2) : ""
    if (this.hasAnnualFeeFieldTarget)    this.annualFeeFieldTarget.value    = fee.annual.toFixed(2)
    if (this.hasHoursSavedPmFieldTarget) this.hoursSavedPmFieldTarget.value = value.backHours > 0 ? value.backHours.toFixed(1) : ""
    if (this.hasRoiMultipleFieldTarget)  this.roiMultipleFieldTarget.value  = (value.valuePerYear > 0 && fee.annual > 0) ? (value.valuePerYear / fee.annual).toFixed(2) : ""
    if (this.hasCppFieldTarget)          this.cppFieldTarget.value          = members > 0 ? fee.cpp.toFixed(4) : ""

    // Survey-style answers — these post to the new demo_requests.survey_*
    // columns so the sales team has the full operational profile on the
    // follow-up call.
    if (this.hasSurveyClubTypeFieldTarget)         this.surveyClubTypeFieldTarget.value         = this.clubType || ""
    if (this.hasSurveyRenewalStyleFieldTarget)     this.surveyRenewalStyleFieldTarget.value     = this.renewalStyle || ""
    if (this.hasSurveyCompetitionCadenceFieldTarget) this.surveyCompetitionCadenceFieldTarget.value = this.competitionCadence || ""
    if (this.hasSurveyReportingStyleFieldTarget)   this.surveyReportingStyleFieldTarget.value   = this.reportingStyle || ""
    const phonePct  = this.intVal(this.phonePctSliderTarget)
    const walkinPct = this.intVal(this.walkinPctSliderTarget)
    // Prefer the dedicated online slider when present; fall back to
    // residual for back-compat with the legacy two-slider layout.
    const onlinePct = this.hasOnlinePctSliderTarget
      ? this.intVal(this.onlinePctSliderTarget)
      : Math.max(0, 100 - phonePct - walkinPct)
    if (this.hasSurveyBookingPhonePctFieldTarget)  this.surveyBookingPhonePctFieldTarget.value  = phonePct
    if (this.hasSurveyBookingWalkinPctFieldTarget) this.surveyBookingWalkinPctFieldTarget.value = walkinPct
    if (this.hasSurveyBookingOnlinePctFieldTarget) this.surveyBookingOnlinePctFieldTarget.value = onlinePct

    this.syncTopTimeSaversInputs(timeSavers)

    if (this.hasBandBreakdownFieldTarget) {
      const bandLines = fee.lines.map(l => `Members ${l.from}–${l.to} × ${currencySymbol()}${l.rate.toFixed(2)}/mo = ${currencySymbol()}${l.monthly.toFixed(2)}/mo`)
      this.bandBreakdownFieldTarget.value = JSON.stringify(bandLines)
    }

    if (this.hasQuestionsFieldTarget) {
      const payload = {
        phone_pct: this.intVal(this.phonePctSliderTarget),
        walkin_pct: this.intVal(this.walkinPctSliderTarget),
        online_pct: this.hasOnlinePctSliderTarget
          ? this.intVal(this.onlinePctSliderTarget)
          : Math.max(0, 100 - this.intVal(this.phonePctSliderTarget) - this.intVal(this.walkinPctSliderTarget)),
        renewal_minutes: this.getRenewalMinutes(),
        comps_per_month: this.getCompsPerMonth(),
        renewal_style: this.renewalStyle,
        competition_cadence: this.competitionCadence,
        reporting_style: this.reportingStyle,
        finance_minutes_per_month: this.getFinanceMinutesPerMonth(),
        manual_finance: this.hasManualFinanceTarget ? this.manualFinanceTarget.checked : null,
        today_hrs_per_month: Number(value.todayHours.toFixed(1)),
        with_hrs_per_month:  Number(value.withHours.toFixed(1)),
        back_hrs_per_month:  Number(value.backHours.toFixed(1)),
        contributions: value.contributions.map(c => ({
          label: c.label,
          hrs_per_month: Number(c.hrsPerMo.toFixed(1)),
          today_hrs: Number(c.todayHrs.toFixed(1)),
          with_hrs:  Number(c.withHrs.toFixed(1))
        })),
        payback_months: (value.valuePerYear > 0 && fee.annual > 0)
          ? Number(((fee.annual / value.valuePerYear) * 12).toFixed(2))
          : null
      }
      this.questionsFieldTarget.value = JSON.stringify(payload)
    }
  }

  async submit(event) {
    event.preventDefault()
    const form = this.formTarget
    const formData = new FormData(form)
    const email = formData.get("demo_request[email]") || ""

    try {
      const response = await fetch(form.action, {
        method: "POST",
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
        },
        body: formData
      })
      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        const msg = (data.errors && data.errors.join(", ")) || "Something went wrong. Please try again."
        alert(msg)
        return
      }
    } catch (_) { /* fall through and show confirmation */ }

    this.submitted = true
    this.formAreaTarget.classList.add("hidden")
    this.confirmTarget.classList.remove("hidden")
    if (email) this.confirmMsgTarget.textContent = `Sent to ${email} — check your inbox.`
  }
}
